import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, Loader2, Plug } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { PROVIDER_ICONS_SMALL, FallbackIcon } from '@/frontend/components/DigitalWorkers/providerIcons'
import CategoryFilter from '@/frontend/components/Integrations/CategoryFilter'
import ComposioAppIcon from '@/frontend/components/Integrations/ComposioAppIcon'
import type {
  AvailableIntegration,
  IntegrationToolkit,
} from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  availableIntegrations: AvailableIntegration[]
  integrationToolkits: IntegrationToolkit[]
  categories: string[]
  connectedProviders: string[]
  canManage: boolean
  busyKey: string | null
  onAssignIntegration: (integration: AvailableIntegration) => void
  onConnectProvider: (provider: string) => void
  onConnectToolkit: (slug: string) => void
}

const BUILTIN_DESCRIPTIONS: Record<string, string> = {
  meta: 'Facebook Ads, Instagram e catálogos de produtos',
  google: 'Google Ads, Analytics e Search Console',
  slack: 'Mensagens e notificações em canais',
  notion: 'Bases de dados, documentos e wikis',
}

interface ModalItem {
  key: string
  label: string
  description: string
  mode: 'assign' | 'connect'
  provider: string
  iconKind: 'builtin' | 'toolkit'
  categories: string[]
  logo: string | null
  actionKey: string
}

function matchesQuery(values: Array<string | undefined>, query: string) {
  if (!query) return true
  return values.some((value) => value?.toLowerCase().includes(query))
}

export default function IntegrationConnectModal({
  open,
  onOpenChange,
  availableIntegrations,
  integrationToolkits,
  categories,
  connectedProviders,
  canManage,
  busyKey,
  onAssignIntegration,
  onConnectProvider,
  onConnectToolkit,
}: Props) {
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const query = search.trim().toLowerCase()

  useEffect(() => {
    if (!open) return
    window.setTimeout(() => searchInputRef.current?.focus(), 80)
  }, [open])

  const items = useMemo<ModalItem[]>(() => {
    const assignableItems = availableIntegrations
      .filter((integration) => integration.status === 'assignable')
      .map((integration) => ({
        key: `assign:${integration.id || integration.provider}`,
        label: integration.label,
        description: 'Já disponível na organização para este colaborador.',
        mode: 'assign' as const,
        provider: integration.provider,
        iconKind: 'builtin' as const,
        categories: integration.categories,
        logo: null,
        actionKey: `assign:${integration.id || integration.provider}`,
      }))

    const connectableProviderItems = availableIntegrations
      .filter((integration) => integration.status === 'connectable')
      .map((integration) => ({
        key: `provider:${integration.provider}`,
        label: integration.label,
        description: BUILTIN_DESCRIPTIONS[integration.provider] || 'Conexão de dados para o colaborador.',
        mode: 'connect' as const,
        provider: integration.provider,
        iconKind: 'builtin' as const,
        categories: integration.categories,
        logo: null,
        actionKey: `provider:${integration.provider}`,
      }))

    const connectableToolkitItems = integrationToolkits
      .filter((toolkit) => !connectedProviders.includes(toolkit.slug))
      .map((toolkit) => ({
        key: `toolkit:${toolkit.slug}`,
        label: toolkit.name,
        description: toolkit.description,
        mode: 'connect' as const,
        provider: toolkit.slug,
        iconKind: 'toolkit' as const,
        categories: toolkit.categories,
        logo: toolkit.logo,
        actionKey: `toolkit:${toolkit.slug}`,
      }))

    return [...assignableItems, ...connectableProviderItems, ...connectableToolkitItems]
      .filter((item) => matchesQuery([item.label, item.provider, item.description], query))
      .filter((item) => selectedCategory ? item.categories.includes(selectedCategory) : true)
      .sort((left, right) => {
        const labelComparison = left.label.localeCompare(right.label, 'pt-BR')
        if (labelComparison !== 0) return labelComparison
        if (left.mode === right.mode) return 0
        return left.mode === 'assign' ? -1 : 1
      })
  }, [availableIntegrations, connectedProviders, integrationToolkits, query, selectedCategory])

  const visibleCategories = useMemo(() => {
    const presentCategories = new Set(
      [...availableIntegrations.flatMap((integration) => integration.categories), ...integrationToolkits.flatMap((toolkit) => toolkit.categories)],
    )

    return categories.filter((category) => presentCategories.has(category))
  }, [availableIntegrations, categories, integrationToolkits])

  useEffect(() => {
    if (selectedCategory && !visibleCategories.includes(selectedCategory)) {
      setSelectedCategory(null)
    }
  }, [selectedCategory, visibleCategories])

  const renderBuiltinIcon = (provider: string) => PROVIDER_ICONS_SMALL[provider] || <FallbackIcon />

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex min-h-0 max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="shrink-0 border-b border-border px-6 pb-4 pt-6">
          <DialogTitle>Adicionar integração</DialogTitle>
          <DialogDescription>
            Busque pela ferramenta que você quer conectar ao colaborador.
          </DialogDescription>

          <div className="relative pt-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar integrações"
                  aria-label="Buscar integrações"
                  className="pl-9"
                />
              </div>

              {visibleCategories.length > 6 && (
                <CategoryFilter
                  categories={visibleCategories}
                  selected={selectedCategory}
                  onSelect={setSelectedCategory}
                />
              )}
            </div>

            {visibleCategories.length > 0 && visibleCategories.length <= 6 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setSelectedCategory(null)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-all',
                    selectedCategory === null
                      ? 'border-brand bg-brand/10 text-brand'
                      : 'border-border bg-card text-muted-foreground hover:border-brand/60 hover:text-foreground',
                  )}
                >
                  Todas
                </button>
                {visibleCategories.map((category) => {
                  const active = selectedCategory === category
                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setSelectedCategory(active ? null : category)}
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs font-medium transition-all',
                        active
                          ? 'border-brand bg-brand/10 text-brand'
                          : 'border-border bg-card text-muted-foreground hover:border-brand/60 hover:text-foreground',
                      )}
                    >
                      {category}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4" data-testid="integration-connect-modal">

          {items.length > 0 ? (
            <div className="space-y-2">
              {items.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center gap-3 rounded-lg border border-border px-3 py-3"
                >
                  <div className="shrink-0">
                    {item.iconKind === 'toolkit'
                      ? <ComposioAppIcon logo={item.logo} name={item.label} className="h-5 w-5" />
                      : renderBuiltinIcon(item.provider)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>

                  {item.mode === 'assign' ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const integration = availableIntegrations.find((candidate) => candidate.status === 'assignable' && (`assign:${candidate.id || candidate.provider}`) === item.key)
                        if (integration) onAssignIntegration(integration)
                      }}
                      disabled={!canManage || Boolean(busyKey)}
                    >
                      {busyKey === item.actionKey
                        ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        : <Plug className="mr-1 h-4 w-4" />}
                      Associar
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (item.iconKind === 'toolkit') onConnectToolkit(item.provider)
                        else onConnectProvider(item.provider)
                      }}
                      disabled={!canManage || Boolean(busyKey)}
                    >
                      {busyKey === item.actionKey
                        ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        : <Plug className="mr-1 h-4 w-4" />}
                      Associar
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">Nenhuma integração bate com o filtro.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
